import type { Editor, Range, SuggestionListRef } from 'OpalChat/TiptapEditorTypes';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { Box, Flex, Separator, Text } from '@optiaxiom/react';
import { IconBracketsCurly, IconPlus } from '@optimizely/axiom-icons';

import type { SuggestionItem } from '../types';

import { useSuggestionKeyboard } from './hooks/useSuggestionKeyboard';
import { styles } from './AgentFormPromptTemplate.css';
import { SectionHeader, SuggestionItem as SuggestionItemComponent } from './Common';

const INSTRUCTIONS_ITEM = {
  id: 'action:add-instructions',
  label: 'Add instructions',
  searchTerms: ['add instructions', 'instruction'],
};

const RETRIEVAL_ITEMS = [
  {
    id: 'action:add-retrieval',
    label: 'Retrieve text',
    searchTerms: ['add retrieval', 'retrieval', 'rag', 'text'],
  },
  {
    id: 'action:add-retrieval-files',
    label: 'Retrieve text and files',
    searchTerms: ['add retrieval files', 'retrieval', 'rag', 'files'],
  },
] as const;

export type InstructionsSuggestionListProps = {
  editor: Editor;
  range: Range;
  command: (item: SuggestionItem) => void;
  query: string;
  onInsertInstructions: (editor: Editor) => void;
  onInsertRetrieval: (editor: Editor) => void;
  onInsertRetrievalWithFiles: (editor: Editor) => void;
};

export const InstructionsSuggestionList = forwardRef<
  SuggestionListRef,
  InstructionsSuggestionListProps
>(
  (
    {
      editor,
      range,
      command,
      query,
      onInsertInstructions,
      onInsertRetrieval,
      onInsertRetrievalWithFiles,
    },
    ref
  ) => {
    const lowerQuery = query?.toLowerCase() ?? '';

    const showInstructions =
      !lowerQuery || INSTRUCTIONS_ITEM.searchTerms.some(term => term.includes(lowerQuery));

    const filteredRetrievalItems = useMemo(
      () =>
        RETRIEVAL_ITEMS.filter(
          item => !lowerQuery || item.searchTerms.some(term => term.includes(lowerQuery))
        ),
      [lowerQuery]
    );

    const suggestionItems: SuggestionItem[] = useMemo(
      () => [
        ...(showInstructions
          ? [
              {
                id: INSTRUCTIONS_ITEM.id,
                label: INSTRUCTIONS_ITEM.label,
                type: 'action' as const,
                actionType: 'add-instructions' as const,
              },
            ]
          : []),
        ...filteredRetrievalItems.map(item => ({
          id: item.id,
          label: item.label,
          type: 'action' as const,
          actionType: item.id.replace('action:', '') as 'add-retrieval' | 'add-retrieval-files',
        })),
      ],
      [showInstructions, filteredRetrievalItems]
    );

    const handleSelect = useCallback(
      (item: SuggestionItem) => {
        editor.chain().focus().deleteRange(range).run();
        switch (item.actionType) {
          case 'add-instructions':
            onInsertInstructions(editor);
            break;
          case 'add-retrieval':
            onInsertRetrieval(editor);
            break;
          case 'add-retrieval-files':
            onInsertRetrievalWithFiles(editor);
            break;
        }
        command(item);
      },
      [editor, range, command, onInsertInstructions, onInsertRetrieval, onInsertRetrievalWithFiles]
    );

    const { selectedId, onKeyDown, handleItemClick } = useSuggestionKeyboard({
      items: suggestionItems,
      query,
      onSelect: handleSelect,
    });

    useImperativeHandle(ref, () => ({ onKeyDown }), [onKeyDown]);

    if (suggestionItems.length === 0) {
      return (
        <Box
          rounded="md"
          shadow="md"
          border="1"
          borderColor="border.default"
          bg="bg.default"
          p="6"
          className={styles.container}
          maxH="md"
        >
          <Text textAlign="center" color="fg.tertiary" fontSize="sm">
            No results found
          </Text>
        </Box>
      );
    }

    const hasRetrievalItems = filteredRetrievalItems.length > 0;

    return (
      <Flex
        flexDirection="column"
        gap="6"
        rounded="md"
        shadow="md"
        border="1"
        borderColor="border.default"
        overflow="auto"
        bg="bg.default"
        pt="6"
        pb="12"
        className={styles.container}
      >
        {showInstructions && (
          <>
            <SectionHeader title="Instructions" />
            <SuggestionItemComponent
              iconBefore={<IconPlus />}
              label="Add instructions"
              isAction
              isSelected={selectedId === INSTRUCTIONS_ITEM.id}
              onClick={() => handleItemClick(INSTRUCTIONS_ITEM.id)}
            />
          </>
        )}
        {hasRetrievalItems && (
          <>
            {showInstructions && <Separator />}
            <SectionHeader title="Retrieval (RAG)" />
            {filteredRetrievalItems.map(item => (
              <SuggestionItemComponent
                key={item.id}
                iconBefore={<IconBracketsCurly />}
                label={item.label}
                isAction
                isSelected={selectedId === item.id}
                onClick={() => handleItemClick(item.id)}
              />
            ))}
          </>
        )}
      </Flex>
    );
  }
);

InstructionsSuggestionList.displayName = 'InstructionsSuggestionList';
